import { FC } from 'react';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import InfoOutline from '@mui/icons-material/InfoOutline';
import { Controller } from 'react-hook-form';
import { KybRequestProcess } from 'further-types/lp/kyb';
import { Column, Row } from 'further-ui/components/Layout';
import BasicSelect from 'further-ui/components/BasicSelect';
import { kybOnboardingStepDescription } from 'further-ui/labels';
import useKybSettings from './useKybSettings';
import BasicCard from '../BasicCard';
import DefaultKybRequests from './DefaultKybRequests';
import CustomKybRequests from './CustomKybRequests';

type Props = {
  vehicleId: string;
  disabled?: boolean;
};

const KybSettings: FC<Props> = ({ vehicleId, disabled }) => {
  const {
    control,
    kybRequestProcess,
    kybRequestProcessOptions,
    handleKybRequestProcessChange,
  } = useKybSettings(vehicleId);

  return (
    <Column fullWidth spacing="lg">
      <Column spacing="sm">
        <Row centered spacing="xxs">
          <Typography variant="h6">KYB information request process</Typography>
          <Tooltip title="By default the system automatically requests constitutional documents and a registry extract showing entity beneficial ownership. If you wish to customise the document request, select the custom request form and enter the custom fields you require below. If you wish a third party to complete KYB on your behalf, select third party redirection and enter their URL below.">
            <InfoOutline fontSize="inherit" />
          </Tooltip>
        </Row>
        <Controller
          control={control}
          name="kybRequestProcess"
          render={({ field }) => (
            <BasicSelect
              options={kybRequestProcessOptions}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                handleKybRequestProcessChange(value);
              }}
              disabled={disabled}
            />
          )}
        />
      </Column>
      <Divider />
      <BasicCard
        title="KYB information requests"
        description="Set up KYB onboarding requests required from investors. Create individual requests, group related ones into sections, and adjust their order with drag and drop to align with your review process."
      />
      <BasicCard
        title="KYB information"
        description={kybOnboardingStepDescription}
        outlined
      />
      <Divider />
      {kybRequestProcess === KybRequestProcess.SystemDefaultForm ? (
        <DefaultKybRequests />
      ) : null}
      {kybRequestProcess === KybRequestProcess.CustomRequestForm ? (
        <CustomKybRequests vehicleId={vehicleId} disabled={disabled} />
      ) : null}
    </Column>
  );
};

export default KybSettings;
